import { fetchPageData } from "~/app/api/pageData/pageDataClient";
import { pageDataKeys } from "~/app/api/pageData/pageDataKeys";
import { sessionQueryOptions } from "~/app/api/pageData/sessionQuery";
import { queryClient } from "~/app/queryClient";
import type { SessionCartIdentityData, SessionData } from "~/app/store/bffApi";
import { getSessionSelectedAccount, getSessionUserSnapshot } from "~/domains/user/sessionRuntime";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { createCartForAdd, isCartAlreadyExistsForContext } from "~/features/cart/cart/api/CartCreation";
import { cartRequestContextKey, type CartRequestContext } from "~/features/cart/cart/api/CartRequestContext";

export interface CartContext {
  contextKey: string;
  requestContext: CartRequestContext;
  identity: SessionCartIdentityData;
  resourceId: string;
}

const activeCartEnsurePromises = new Map<string, Promise<CartContext>>();

/**
 * Obtains the authorized Active Cart for the current commercial context.
 * Only the explicit add flow calls this function, so a missing cart may be created here.
 */
export async function ensureActiveCartForContext(): Promise<CartContext> {
  const requestContext = captureCartRequestContext();
  return ensureActiveCart(requestContext, captureCartRequestContext);
}

/**
 * Ensures an authenticated Active Cart from one explicit Session Data snapshot.
 * The current Session query remains the concurrency/context-change authority; runtime globals are not consulted.
 */
export async function ensureAuthenticatedActiveCartForSession(session: SessionData): Promise<CartContext> {
  const requestContext = captureAuthenticatedCartRequestContext(session);
  assertCurrentContext(requestContext, captureCurrentAuthenticatedCartRequestContext);
  return ensureActiveCart(
    requestContext,
    captureCurrentAuthenticatedCartRequestContext,
    captureAuthenticatedCartRequestContext,
    session,
  );
}

async function ensureActiveCart(
  requestContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext,
  captureFetchedContext?: (session: SessionData) => CartRequestContext,
  initialSession?: SessionData,
): Promise<CartContext> {
  const contextKey = cartRequestContextKey(requestContext);
  const ongoingEnsure = activeCartEnsurePromises.get(contextKey);
  if (ongoingEnsure) {
    return ongoingEnsure;
  }

  const ensurePromise = obtainActiveCart(requestContext, captureCurrentContext, captureFetchedContext, initialSession);
  activeCartEnsurePromises.set(contextKey, ensurePromise);

  try {
    return await ensurePromise;
  } finally {
    if (activeCartEnsurePromises.get(contextKey) === ensurePromise) {
      activeCartEnsurePromises.delete(contextKey);
    }
  }
}

async function obtainActiveCart(
  requestContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext,
  captureFetchedContext?: (session: SessionData) => CartRequestContext,
  initialSession?: SessionData,
): Promise<CartContext> {
  const session =
    initialSession ?? (await fetchFreshSession(requestContext, captureCurrentContext, captureFetchedContext));
  const activeCart = toCartContext(requestContext, session.activeCart);
  return activeCart ?? createOrRecoverActiveCart(requestContext, captureCurrentContext, captureFetchedContext);
}

async function createOrRecoverActiveCart(
  requestContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext,
  captureFetchedContext?: (session: SessionData) => CartRequestContext,
): Promise<CartContext> {
  let createdIdentity: SessionCartIdentityData | undefined;
  try {
    createdIdentity = await createCartForAdd(requestContext);
    assertCurrentContext(requestContext, captureCurrentContext);
    projectActiveCart(createdIdentity);
  } catch (error) {
    if (!isCartAlreadyExistsForContext(error)) {
      throw error;
    }
  }

  try {
    const session = await refreshSession(requestContext, captureCurrentContext, captureFetchedContext);
    const activeCart = toCartContext(requestContext, session.activeCart);
    if (!activeCart) {
      throw new Error("Cart ensure did not resolve to an explicit Active Cart");
    }
    if (createdIdentity && activeCart.resourceId !== resourceId(createdIdentity, requestContext.authenticated)) {
      throw new Error("Cart ensure refreshed a different Active Cart than the cart it created");
    }
    return activeCart;
  } catch (error) {
    if (createdIdentity) {
      clearProjectedActiveCart(createdIdentity);
    }
    throw error;
  }
}

function captureCartRequestContext(): CartRequestContext {
  const selectedAccount = getSessionSelectedAccount();
  const siteUid = window.__SCHA_BOOT__?.siteUid;
  const authenticated = userIsLoggedIn();
  const userId = authenticated ? getSessionUserSnapshot().uid : null;
  if (!siteUid) {
    throw new Error("Cannot ensure an Active Cart without a site identity");
  }
  if (authenticated && !userId) {
    throw new Error("Cannot ensure an Active Cart without an authenticated user identity");
  }
  return {
    siteUid,
    language: window.__SCHA_BOOT__?.language || null,
    authenticated,
    userId: userId || null,
    soldTo: selectedAccount?.uid || null,
    salesOrg: selectedAccount?.salesOrg || null,
    endCustomer: selectedAccount?.endCustomerCode || null,
  };
}

function captureAuthenticatedCartRequestContext(session: SessionData): CartRequestContext {
  const siteUid = window.__SCHA_BOOT__?.siteUid;
  const userId = session.user.uid;
  const selectedAccount = session.commerceContext.selectedAccount;
  if (!siteUid) {
    throw new Error("Cannot ensure an Active Cart without a site identity");
  }
  if (!userId) {
    throw new Error("Cannot ensure an Active Cart without an authenticated user identity");
  }
  if (!selectedAccount?.uid || !selectedAccount.salesOrg) {
    throw new Error("Cannot ensure an Active Cart without an explicit account context");
  }
  return {
    siteUid,
    language: window.__SCHA_BOOT__?.language || null,
    authenticated: true,
    userId,
    soldTo: selectedAccount.uid,
    salesOrg: selectedAccount.salesOrg,
    endCustomer: selectedAccount.endCustomerCode || null,
  };
}

function captureCurrentAuthenticatedCartRequestContext(): CartRequestContext {
  const session = queryClient.getQueryData<SessionData>(pageDataKeys.session());
  if (!session) {
    throw new Error("Cart context changed while resolving the Active Cart");
  }
  return captureAuthenticatedCartRequestContext(session);
}

export function assertCurrentCartContext(cart: CartContext): void {
  assertCurrentContext(cart.requestContext);
}

function assertCurrentContext(
  expectedContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext = captureCartRequestContext,
): void {
  if (cartRequestContextKey(captureCurrentContext()) !== cartRequestContextKey(expectedContext)) {
    throw new Error("Cart context changed while resolving the Active Cart");
  }
}

function resourceId(identity: SessionCartIdentityData, authenticated: boolean): string {
  const id = authenticated ? identity.code : identity.guid;
  if (!id) {
    throw new Error("Active Cart does not expose the required resource identity");
  }
  return id;
}

function toCartContext(
  requestContext: CartRequestContext,
  identity: SessionCartIdentityData | null,
): CartContext | undefined {
  return identity
    ? {
        contextKey: cartRequestContextKey(requestContext),
        requestContext,
        identity,
        resourceId: resourceId(identity, requestContext.authenticated),
      }
    : undefined;
}

function projectActiveCart(identity: SessionCartIdentityData): void {
  queryClient.setQueryData<SessionData>(pageDataKeys.session(), (session) =>
    session ? { ...session, activeCart: identity } : session,
  );
}

function clearProjectedActiveCart(identity: SessionCartIdentityData): void {
  queryClient.setQueryData<SessionData>(pageDataKeys.session(), (session) => {
    if (session?.activeCart?.code !== identity.code || session.activeCart.guid !== identity.guid) {
      return session;
    }
    return { ...session, activeCart: null };
  });
}

async function fetchFreshSession(
  requestContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext,
  captureFetchedContext?: (session: SessionData) => CartRequestContext,
): Promise<SessionData> {
  const { retry } = sessionQueryOptions();
  const session = await queryClient.fetchQuery({
    gcTime: 0,
    queryKey: [...pageDataKeys.session(), "cart-context", cartRequestContextKey(requestContext)] as const,
    queryFn: () => fetchPageData<SessionData>("/page-data/session"),
    retry,
    staleTime: 0,
  });
  if (
    captureFetchedContext &&
    cartRequestContextKey(captureFetchedContext(session)) !== cartRequestContextKey(requestContext)
  ) {
    throw new Error("Cart context changed while resolving the Active Cart");
  }
  assertCurrentContext(requestContext, captureCurrentContext);
  queryClient.setQueryData(pageDataKeys.session(), session);
  return session;
}

async function refreshSession(
  requestContext: CartRequestContext,
  captureCurrentContext: () => CartRequestContext,
  captureFetchedContext?: (session: SessionData) => CartRequestContext,
): Promise<SessionData> {
  await queryClient.invalidateQueries({
    exact: true,
    queryKey: pageDataKeys.session(),
    refetchType: "none",
  });
  return fetchFreshSession(requestContext, captureCurrentContext, captureFetchedContext);
}
